import {
    Button,
    Col,
    Descriptions,
    type DescriptionsProps,
    Divider,
    Flex,
    Row,
    Space,
    Table,
    Tag,
    Typography
} from 'antd'
import Text from 'antd/es/typography/Text'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { UserParams } from '@services/UserService'
import { UserService } from '@services/UserService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { MultiFactorInfo, Product, Subscription, User } from '../../../../../types'
import { EditUserModal } from '../../../../components/EditUserModal'
import AddSubscriptionModal from '../../components/AddSubscriptionModal'
import { EditSubscriptionModal } from '../../components/EditSubscriptionModal'
import { SubscriptionCard } from '../../components/SubscriptionCards'

import { ProductUserCard } from './ProductUserCard'

const mfaSchoolRequirementTableColumns = [
    {
        title: 'School',
        dataIndex: 'school',
        key: 'school'
    },
    {
        title: 'School User Role',
        dataIndex: 'role',
        key: 'role'
    },
    {
        title: 'MFA Requirement',
        dataIndex: 'mfaLevel',
        key: 'mfaLevel'
    },
    {
        title: 'Required By Date',
        dataIndex: 'date',
        key: 'date'
    }
]

export const DetailsTab = ({
    user,
    products,
    mfaInfo,
    refetch
}: {
    user: User
    products: Product[]
    mfaInfo?: MultiFactorInfo[]
    refetch: () => Promise<void>
}) => {
    const { showNotification } = useNotifications()
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isAddSubscriptionModalOpen, setIsAddSubscriptionModalOpen] = useState(false)
    const [subscriptionToEdit, setSubscriptionToEdit] = useState<Subscription | null>(null)

    const navigate = useNavigate()
    const confirm = useConfirmation()

    const { userId } = useParams()

    const hasMfa = useMemo(() => mfaInfo && mfaInfo.length > 0, [mfaInfo])

    const onReenableUser = async () => {
        if (!userId) {
            return
        }
        if (
            await confirm({
                title: 'Are you sure you want to re-enable this user?',
                confirmText: 'Enable'
            })
        ) {
            await UserService.enable(userId)

            showNotification({
                title: 'User has been re-enabled'
            })

            refetch()
        }
    }

    const onRemoveProduct = async (productId: string) => {
        if (!userId) {
            return
        }

        if (
            await confirm({
                title: 'Are you sure you want to remove this product?',
                description: 'After removing this product, it can not be added back via the admin',
                confirmText: 'Remove'
            })
        ) {
            await UserService.removeProductUser(userId, productId)

            showNotification({
                title: 'Product has been removed'
            })

            refetch()
        }
    }

    const items: DescriptionsProps['items'] = [
        {
            key: '0',
            label: 'ID',
            children: user?.id
        },
        {
            key: '1',
            label: 'Firebase ID',
            children: user?.firebaseId
        },
        {
            key: '2',
            label: 'Stripe ID',
            children: user?.stripeId
        },
        {
            key: '3',
            label: 'Email',
            children: user?.email
        },
        {
            key: '4',
            label: 'First Name',
            children: user?.firstName
        },
        {
            key: '5',
            label: 'Last Name',
            children: user?.lastName
        },
        {
            key: '6',
            label: 'Role',
            children: user?.role
        },
        {
            key: 'status',
            label: 'Status',
            children: user?.status
        },
        {
            key: '7',
            label: 'User Tag',
            children: user?.userTag
        },
        {
            key: '8',
            label: 'Max Boards',
            children: user?.meta.maxBoards
        },
        {
            key: '9',
            label: 'Currency Type',
            children: user?.meta.currencyType
        },
        {
            key: '10',
            label: 'Is Part of Research Program',
            children: user?.userAgreements?.researchProgram ? 'Yes' : 'No'
        },
        {
            key: '11',
            label: 'Created At',
            children: user?.createdAt ? dayjs(user?.createdAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'
        },
        {
            key: '12',
            label: 'Updated At',
            children: user?.updatedAt ? dayjs(user?.updatedAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'
        },
        {
            key: '13',
            label: 'Last Active At',
            children: user?.lastActiveAt ? dayjs(user?.lastActiveAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'
        }
    ]

    const closeEditModal = () => {
        setIsEditModalOpen(false)
    }

    const clearBoardTourMeta = async () => {
        const body: UserParams = {
            meta: {
                boardToolsWalkthrough: []
            }
        }
        await UserService.update(user.id!, body)
        showNotification({ title: 'Board tour cleared successfully' })
        await refetch()
    }

    const clearMailingList = async () => {
        const body: UserParams = {
            userAgreements: {
                mailingList: null
            }
        }
        await UserService.update(user.id!, body)

        showNotification({ title: 'Mailing List agreement cleared successfully' })
        await refetch()
    }

    return (
        <>
            <Descriptions
                title="User Info"
                items={items}
                column={3}
                extra={
                    <Space>
                        {user?.status === 'DISABLED' && <Button onClick={onReenableUser}>Re-enable</Button>}
                        <Button onClick={() => setIsEditModalOpen(true)}>Edit</Button>
                        <Button onClick={clearBoardTourMeta}>Clear Board Tour Meta</Button>
                        <Button onClick={clearMailingList}>Clear Mailing List Agreement</Button>
                    </Space>
                }
            />
            <Divider />

            <Flex justify="space-between" align="center">
                <Flex gap="small" align="end" justify="end">
                    <Typography.Title level={4}>Multi Factor Authentication</Typography.Title>
                    {hasMfa ? (
                        <Tag color="green" variant="solid" style={{ marginBottom: '12px' }}>
                            Enrolled
                        </Tag>
                    ) : (
                        <Tag color="red" variant="solid" style={{ marginBottom: '12px' }}>
                            Not Enrolled
                        </Tag>
                    )}
                </Flex>
                {hasMfa && (
                    <Button
                        type="primary"
                        color="danger"
                        onClick={async () => {
                            if (
                                await confirm({
                                    title: 'Disable MFA for this user?',
                                    description:
                                        'The user will have to set up MFA again if they wish to use it. Note, this will not affect any existing school user MFA requirements.',
                                    confirmText: 'Disable'
                                })
                            ) {
                                try {
                                    await UserService.disableMFA(userId!)
                                    await refetch()
                                    showNotification({
                                        type: 'success',
                                        title: 'MFA has been successfully disabled.'
                                    })
                                } catch (err) {
                                    showNotification({
                                        type: 'error',
                                        title: 'An error occurred. MFA has not been disabled for this user.'
                                    })
                                }
                            }
                        }}
                    >
                        Disable
                    </Button>
                )}
            </Flex>
            <Flex gap="middle" vertical>
                {mfaInfo &&
                    mfaInfo.map(mfa => {
                        return (
                            <Flex key={mfa.uid} vertical>
                                <Text>Type: {mfa.factorId.toUpperCase()}</Text>
                                <Text>Enrollment Date: {mfa.enrollmentTime}</Text>
                            </Flex>
                        )
                    })}
                {user.schoolUsers && (
                    <>
                        <Typography.Title level={5}>School Requirements</Typography.Title>
                        <Table
                            columns={mfaSchoolRequirementTableColumns}
                            dataSource={user.schoolUsers.map(schoolUser => {
                                const { mfaSchoolUserRequirement } = schoolUser
                                let requiredDate

                                if (mfaSchoolUserRequirement) {
                                    requiredDate = new Date(mfaSchoolUserRequirement.requiredAfter)
                                        .toISOString()
                                        .split('T')[0]
                                }
                                return {
                                    school: schoolUser.school.name,
                                    role: schoolUser.role,
                                    mfaLevel: mfaSchoolUserRequirement
                                        ? mfaSchoolUserRequirement.mfaSchoolRequirement?.mfaLevel
                                        : '-',
                                    date: requiredDate || '-'
                                }
                            })}
                        />
                    </>
                )}
            </Flex>

            <Typography.Title level={4}>Schools</Typography.Title>
            <Flex gap="middle">
                {user?.schoolUsers?.map(su => {
                    return (
                        <Button key={su.id} onClick={() => navigate(`/schools/${su.school.id}`)}>
                            {su.school.name}
                        </Button>
                    )
                })}
            </Flex>

            <Flex justify="space-between" align="center">
                <Typography.Title level={4}>Personal Subscriptions</Typography.Title>
                <Button type="primary" onClick={() => setIsAddSubscriptionModalOpen(true)}>
                    + New Subscription
                </Button>
            </Flex>

            <Row gutter={[16, 16]}>
                {user?.userSubscriptions?.map(({ subscription }) => {
                    if (!subscription) {
                        return null
                    }
                    return (
                        <Col key={subscription.id}>
                            <SubscriptionCard
                                subscription={subscription}
                                onEditSubscription={() => setSubscriptionToEdit(subscription)}
                            />
                        </Col>
                    )
                })}
            </Row>
            {user?.productUsers && user.productUsers.length > 0 && (
                <>
                    <Flex justify="space-between" align="center">
                        <Typography.Title level={4}>Assigned Products</Typography.Title>
                    </Flex>

                    {user?.productUsers?.map(pu => (
                        <ProductUserCard key={pu.id} product={pu.product!} onRemoveProduct={onRemoveProduct} />
                    ))}
                </>
            )}

            <EditUserModal isOpen={isEditModalOpen} user={user} onClose={closeEditModal} refetch={refetch} />

            <AddSubscriptionModal
                isOpen={isAddSubscriptionModalOpen}
                user={user}
                products={products}
                onClose={() => setIsAddSubscriptionModalOpen(false)}
                refetch={refetch}
            />
            <EditSubscriptionModal
                isOpen={!!subscriptionToEdit}
                subscription={subscriptionToEdit}
                products={products}
                refetch={refetch}
                onClose={() => setSubscriptionToEdit(null)}
                user={user}
            />
        </>
    )
}
